import { urlForImage } from 'lib/sanity.image'

import { motion } from 'motion/react'
import Image from 'next/image'
import { PortableText } from '@portabletext/react'
import { CloseIcon } from 'components/Global/CloseIcon'
import { Button } from 'components/Global/Button'
import { useState } from 'react'

export const SizingGuide = ({ title, body, categories, type, onClose }) => {
  const activeCategory = categories.find((cat) => cat.productType === type)
  return (
    <div className="fixed inset-0 z-50 flex h-screen w-full justify-center overflow-x-hidden overflow-y-scroll overscroll-contain p-14">
      <motion.div
        className="fixed inset-0 z-[-1] h-full w-full bg-blackTransparent backdrop-blur-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
        }}
      ></motion.div>
      <div className="w-full max-w-[750px] py-60">
        <motion.div
          className="w-full pb-60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.4,
            ease: 'easeInOut',
          }}
        >
          {/* Header */}
          <div className="z-[1] flex w-full justify-between pb-20 text-11 text-darkGray">
            <div className="flex gap-4 rounded-8 bg-white p-4 shadow-sm">
              <Button
                active
                className="bg-[#DEDEDE] px-12 py-10 text-11 leading-100 md:hover:bg-[#DEDEDE]"
              >
                Size Guide
              </Button>
            </div>
            <div className="flex items-center rounded-8 bg-white p-4 text-11 shadow-sm">
              <Button
                className="h-full px-10 py-12 md:hover:!bg-[#DEDEDE]"
                onClick={onClose}
              >
                <CloseIcon />
              </Button>
            </div>
          </div>

          <div className="w-full rounded-12 bg-white p-20">
            <div className=" text-12 text-midGray md:text-20">{title}</div>
            <div className="mb-20 max-w-[400px] pt-24 text-12 leading-120 text-midGray md:text-14">
              <PortableText value={body} />
            </div>
            {activeCategory?.image ? (
              <div
                className="relative mb-20 bg-midGray"
                style={{ aspectRatio: activeCategory.image.aspect ?? '1/1' }}
              >
                <Image
                  src={urlForImage(activeCategory.image).url()}
                  fill
                  style={{ objectFit: 'cover' }}
                  alt=""
                  placeholder="blur"
                  blurDataURL={activeCategory.image.lqip}
                />
              </div>
            ) : null}
            <SizingTables {...activeCategory} />
          </div>
        </motion.div>
      </div>
    </div>
  )
}

const SizingTables = ({ description, tables }) => {
  const [activeTable, setActiveTable] = useState(tables[0])
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="max-w-[320px] text-14 leading-120 text-midGray">
          {description}
        </div>
        <div className="flex rounded-8 bg-whiter p-4 text-11 text-midGray">
          {tables?.map((table) => {
            return (
              <button
                key={table._key}
                className="relative px-40 py-10 font-medium"
                onClick={() => setActiveTable(table)}
              >
                <span className="relative z-[1]">{table.unit}</span>
                {table.unit === activeTable.unit ? (
                  <motion.div
                    layoutId="indicator"
                    className="absolute inset-0 z-[0] h-full w-full rounded-6 bg-[#DEDEDE]"
                  />
                ) : null}
              </button>
            )
          })}
        </div>
      </div>
      <SizingTable rows={activeTable.table.rows} />
    </div>
  )
}

const SizingTable = ({ rows }) => {
  if (!rows) return null
  return (
    <div className="pt-20 leading-120 text-midGray">
      {rows.map((row, i) => {
        const highlighted = i % 2 !== 0
        const header = i === 0
        return (
          <div
            className={`flex rounded-4 px-8 ${
              header ? 'py-20 text-11' : 'py-4 text-13'
            } ${highlighted ? 'bg-[#F5F5F7]' : ''}`}
            key={`${i}-${row._key}`}
          >
            {row.cells.map((cell) => {
              return (
                <div className="flex-1" key={`${cell}-${i}`}>
                  {cell}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
